import React, { useState, useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Header, Footer } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { 
  Palette, MapPin, Phone, Mail, Globe, Search, Loader2, ArrowLeft, BadgeCheck, 
  Filter, Star, MessageCircle, Play, Image, ExternalLink, ChevronDown, Clock, 
  DollarSign, Users, Send, Crown, Calendar, Video, Music, Brush, Heart, 
  Theater, Sparkles, CheckCircle, Eye
} from 'lucide-react';
import { trackPhoneReveal, trackCallClick } from '../services/analytics';
import { InfoBadge } from '../components/TreatmentInfo';

import { CHILEAN_REGIONS } from '../constants/chileanLocations';
import { API } from '../utils/api';


function getImageUrl(url) {
  if (!url) return null;
  if (url.includes('.emergent.host/api/') || url.includes('.emergentagent.com/api/')) {
    try { url = new URL(url).pathname; } catch(e) {}
  }
  if (url.startsWith('/api/')) {
    var base = process.env.REACT_APP_BACKEND_URL;
    if (base && !base.includes('localhost') && typeof window !== 'undefined' && !base.includes(window.location.hostname)) {
      base = window.location.origin;
    }
    return (base || '') + url;
  }
  return url;
}

const cities = ['Santiago', 'Valparaíso', 'Concepción', 'La Serena', 'Antofagasta', 'Temuco', 'Rancagua', 'Talca', 'Arica', 'Puerto Montt'];

// Helper function to generate URL-friendly slugs
function generateSlug(text) {
  if (!text) return '';
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
    .replace(/[^\w\s-]/g, '') // Remove special chars
    .replace(/[\s_]+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Remove multiple hyphens
    .trim()
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
}

const workshopTypeLabels = {
  teatro: { label: 'Teatro', icon: Theater },
  meditacion: { label: 'Meditación', icon: Heart },
  baile: { label: 'Baile', icon: Music },
  yoga: { label: 'Yoga', icon: Sparkles },
  arte: { label: 'Arte', icon: Brush },
  musica: { label: 'Música', icon: Music },
  deportes: { label: 'Deportes', icon: Users },
  mindfulness: { label: 'Mindfulness', icon: Heart },
  terapia_grupal: { label: 'Terapia Grupal', icon: Users },
  escritura: { label: 'Escritura', icon: Palette },
  otro: { label: 'Otro', icon: Palette }
};

const modalityLabels = {
  presencial: 'Presencial',
  online: 'Online',
  hibrido: 'Híbrido'
};

function StarRating({ rating, onRate, interactive = false, size = 'md' }) {
  var sizeClass = size === 'sm' ? 'w-4 h-4' : size === 'lg' ? 'w-6 h-6' : 'w-5 h-5';
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map(function(star) {
        return (
          <button
            key={star}
            type={interactive ? 'button' : undefined}
            onClick={interactive ? function() { onRate(star); } : undefined}
            className={interactive ? 'cursor-pointer hover:scale-110 transition-transform' : 'cursor-default'}
            disabled={!interactive}
          >
            <Star
              className={sizeClass + ' ' + (star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-slate-600')}
            />
          </button>
        );
      })}
    </div>
  );
}

function WhatsAppButton({ number, name }) {
  if (!number) return null;
  var cleanNumber = number.replace(/\D/g, '');
  if (!cleanNumber.startsWith('56')) cleanNumber = '56' + cleanNumber;
  var message = encodeURIComponent('Hola, los contacto desde sinadicciones.org. Estoy interesado en el taller "' + name + '". ¿Podrían darme más información?');
  var url = 'https://wa.me/' + cleanNumber + '?text=' + message;
  
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-4 bg-green-500/10 border border-green-500/30 rounded-xl hover:bg-green-500/20 transition-colors duration-200 text-green-400" data-testid="workshop-whatsapp-btn">
      <MessageCircle className="w-5 h-5" /><span>WhatsApp</span>
    </a>
  );
}

function GoogleMapsLink({ address, city }) {
  if (!address && !city) return null;
  var query = encodeURIComponent((address || '') + ', ' + (city || '') + ', Chile');
  var url = 'https://www.google.com/maps/search/?api=1&query=' + query;
  
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-4 bg-slate-800 rounded-xl hover:bg-slate-700 transition-colors duration-200 text-slate-300">
      <MapPin className="w-5 h-5 text-emerald-400" /><span>Ver en Google Maps</span><ExternalLink className="w-4 h-4 ml-auto" />
    </a>
  );
}

function YouTubeEmbed({ url }) {
  if (!url) return null;
  var videoId = '';
  if (url.includes('youtube.com/watch')) {
    videoId = url.split('v=')[1]?.split('&')[0];
  } else if (url.includes('youtu.be/')) {
    videoId = url.split('youtu.be/')[1]?.split('?')[0];
  }
  if (!videoId) return null;
  return (
    <div className="aspect-video rounded-xl overflow-hidden bg-slate-800">
      <iframe
        src={'https://www.youtube.com/embed/' + videoId}
        title="Video del taller"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="w-full h-full"
      />
    </div>
  );
}

// Workshop Card Component
function WorkshopCard({ workshop }) {
  var typeInfo = workshopTypeLabels[workshop.workshop_type] || workshopTypeLabels.otro;
  var TypeIcon = typeInfo.icon;
  var workshopSlug = workshop.slug || workshop.workshop_id;

  return (
    <Link to={'/talleres/' + workshopSlug} data-testid={'workshop-card-' + workshop.workshop_id}>
      <Card className="bg-white border-slate-200 hover:border-emerald-500/50 hover:shadow-lg transition-all duration-300 h-full overflow-hidden group">
        {/* Image/Logo */}
        <div className="aspect-video bg-slate-100 relative overflow-hidden">
          {workshop.logo ? (
            <img src={getImageUrl(workshop.logo)} alt={workshop.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <TypeIcon className="w-16 h-16 text-slate-300" />
            </div>
          )}
          {/* Badges */}
          <div className="absolute top-3 left-3 flex gap-2">
            {(workshop.is_premium || workshop.plan_level > 0) && (
              <span className="px-2 py-1 bg-amber-500 text-white text-xs font-bold rounded-md flex items-center gap-1 shadow-md">
                <Crown className="w-3 h-3" /> Premium
              </span>
            )}
            {workshop.is_verified && (
              <span className="px-2 py-1 bg-emerald-500 text-white text-xs font-bold rounded-md flex items-center gap-1 shadow-md">
                <BadgeCheck className="w-3 h-3" /> Verificado
              </span>
            )}
          </div>
          {/* Modality badge with info popup */}
          <div className="absolute bottom-3 right-3">
            <InfoBadge tipo="modalidad" valor={workshop.modality || 'presencial'} />
          </div>
        </div>
        
        <CardContent className="p-5">
          {/* Type Badge */}
          <div className="flex items-center gap-2 mb-3">
            <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-md flex items-center gap-1">
              <TypeIcon className="w-3 h-3" />
              {typeInfo.label}
            </span>
            {workshop.is_free && (
              <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-md">
                Gratuito
              </span>
            )}
          </div>
          
          {/* Name */}
          <h3 className="font-semibold text-lg text-slate-800 mb-2 line-clamp-2 group-hover:text-emerald-600 transition-colors">
            {workshop.name}
          </h3>
          
          {/* Description */}
          {workshop.description && (
            <p className="text-slate-600 text-sm line-clamp-2 mb-3">{workshop.description}</p>
          )}
          
          {/* Location */}
          {workshop.city && (
            <div className="flex items-center gap-2 text-slate-600 text-sm mb-3">
              <MapPin className="w-4 h-4" />
              <span>{workshop.city}</span>
            </div>
          )}
          
          {/* Schedule */}
          {workshop.schedule && (
            <div className="flex items-center gap-2 text-slate-600 text-sm mb-3">
              <Calendar className="w-4 h-4" />
              <span className="line-clamp-1">{workshop.schedule}</span>
            </div>
          )}
          
          {/* Price and Rating */}
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-200">
            {workshop.is_free ? (
              <span className="text-green-600 font-medium">Gratuito</span>
            ) : workshop.price ? (
              <span className="text-emerald-600 font-medium">
                ${workshop.price.toLocaleString('es-CL')}
                {workshop.price_description && <span className="text-slate-500 text-xs ml-1">/{workshop.price_description}</span>}
              </span>
            ) : (
              <span className="text-slate-500 text-sm">Consultar precio</span>
            )}
            
            {workshop.average_rating > 0 && (
              <div className="flex items-center gap-1">
                <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                <span className="text-slate-700 text-sm">{workshop.average_rating.toFixed(1)}</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

// Main Directory Component
export function WorkshopsDirectory() {
  var [searchParams, setSearchParams] = useSearchParams();
  var [workshops, setWorkshops] = useState([]);
  var [loading, setLoading] = useState(true);
  var [total, setTotal] = useState(0);
  var [page, setPage] = useState(1);
  var [totalPages, setTotalPages] = useState(1);
  var [filtersOpen, setFiltersOpen] = useState(false);
  var [workshopTypes, setWorkshopTypes] = useState([]);
  
  // Filters
  var [search, setSearch] = useState('');
  var [region, setRegion] = useState(searchParams.get('region') || '');
  var [workshopType, setWorkshopType] = useState(searchParams.get('workshop_type') || searchParams.get('type') || '');
  var [modality, setModality] = useState(searchParams.get('modality') || '');
  var [isFree, setIsFree] = useState(searchParams.get('free') === 'true' ? true : null);
  var [initialized, setInitialized] = useState(false);

  useEffect(function() {
    setInitialized(true);
    loadWorkshopTypes();
  }, []);

  useEffect(function() {
    if (!initialized) return;
    loadWorkshops();
  }, [initialized, page, region, workshopType, modality, isFree]);

  async function loadWorkshopTypes() {
    try {
      var res = await axios.get(API + '/workshops/types');
      setWorkshopTypes(res.data.types || []);
    } catch (e) {
      console.error(e);
    }
  }

  async function loadWorkshops() {
    setLoading(true);
    try {
      var params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 12);
      if (search) params.append('search', search);
      if (region && region !== 'all') params.append('region', region);
      if (workshopType && workshopType !== 'all') params.append('workshop_type', workshopType);
      if (modality && modality !== 'all') params.append('modality', modality);
      if (isFree !== null) params.append('is_free', isFree);
      
      var res = await axios.get(API + '/workshops/public?' + params.toString());
      setWorkshops(res.data.workshops || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.pages || 1);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  function handleSearch(e) {
    e.preventDefault();
    setPage(1);
    loadWorkshops();
  }

  function clearFilters() {
    setSearch('');
    setRegion('');
    setWorkshopType('');
    setModality('');
    setIsFree(null);
    setPage(1);
  }

  return (
    <div className="min-h-screen bg-slate-950" data-testid="workshops-directory">
      
        <title>Talleres de Rehabilitación en Chile 2026 | Arte, Yoga, Meditación | Sin Adicciones</title>
        <meta name="description" content="Encuentra talleres de rehabilitación de adicciones en Chile: arte, teatro, meditación, yoga, música y más. Actividades terapéuticas complementarias para tu recuperación." />
        <meta name="keywords" content="talleres rehabilitación, arteterapia, yoga adicciones, meditación, musicoterapia, talleres terapéuticos Chile" />
        <link rel="canonical" href="https://sinadicciones.org/talleres" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Talleres de Rehabilitación en Chile | Sin Adicciones" />
        <meta property="og:description" content="Directorio de talleres terapéuticos: arte, yoga, meditación, música y más para complementar tu tratamiento de adicciones." />
        <meta property="og:url" content="https://sinadicciones.org/talleres" />
        <meta property="og:image" content="https://sinadicciones.org/og-image.png" />
        <meta property="og:image:width" content="1280" />
        <meta property="og:image:height" content="896" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Talleres de Rehabilitación en Chile" />
        <meta name="twitter:description" content="Directorio de talleres terapéuticos: arte, yoga, meditación y más." />
        <meta name="twitter:image" content="https://sinadicciones.org/og-image.png" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": "Talleres de Rehabilitación en Chile",
          "description": "Directorio de talleres terapéuticos para complementar el tratamiento de adicciones",
          "numberOfItems": total,
          "itemListElement": workshops.slice(0, 10).map(function(ws, index) {
            return {
              "@type": "ListItem",
              "position": index + 1,
              "item": {
                "@type": "Event",
                "name": ws.name,
                "description": ws.description || "Taller terapéutico de rehabilitación",
                "url": "https://sinadicciones.org/talleres/" + ws.workshop_id,
                "location": ws.city ? { "@type": "Place", "name": ws.city, "address": { "@type": "PostalAddress", "addressLocality": ws.city, "addressCountry": "CL" }} : undefined,
                "eventAttendanceMode": ws.modality === 'online' ? "https://schema.org/OnlineEventAttendanceMode" : "https://schema.org/OfflineEventAttendanceMode"
              }
            };
          })
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "¿Qué son los talleres terapéuticos en rehabilitación de adicciones?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Los talleres terapéuticos son actividades complementarias al tratamiento de adicciones que utilizan el arte, la música, el movimiento corporal, la meditación y otras disciplinas para promover la recuperación emocional y el bienestar. Ayudan a desarrollar nuevas habilidades, manejar el estrés y encontrar formas saludables de expresión."
              }
            },
            {
              "@type": "Question",
              "name": "¿Qué tipos de talleres existen para personas en recuperación?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Existen diversos tipos de talleres: arteterapia (pintura, escultura), musicoterapia, teatro y expresión corporal, yoga y meditación, mindfulness, escritura terapéutica, deportes y actividades al aire libre, y terapia grupal. Cada uno ofrece beneficios diferentes según las necesidades de la persona."
              }
            },
            {
              "@type": "Question",
              "name": "¿Son efectivos los talleres como complemento al tratamiento de adicciones?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Sí, numerosos estudios demuestran que los talleres terapéuticos mejoran significativamente los resultados del tratamiento. Ayudan a reducir la ansiedad y depresión, desarrollar habilidades sociales, encontrar nuevos intereses y pasatiempos saludables, y construir una red de apoyo con otras personas en recuperación."
              }
            },
            {
              "@type": "Question",
              "name": "¿Cuánto cuestan los talleres de rehabilitación en Chile?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Los precios varían según el tipo de taller y modalidad. Existen talleres gratuitos ofrecidos por organizaciones sin fines de lucro y municipalidades. Los talleres privados cuestan entre $20.000 y $50.000 por sesión. Muchos centros de rehabilitación incluyen talleres en sus programas de tratamiento."
              }
            },
            {
              "@type": "Question",
              "name": "¿Puedo participar en talleres online desde cualquier lugar de Chile?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Sí, muchos talleres ofrecen modalidad online o híbrida, permitiendo participar desde cualquier ciudad de Chile. Los talleres de meditación, mindfulness, escritura terapéutica y algunos de arte se adaptan muy bien al formato virtual. Filtra por 'Online' en nuestro directorio para encontrarlos."
              }
            }
          ]
        })}</script>
      
      
      <Header />
      
      {/* Hero Section - Compact on mobile */}
      <section className="pt-20 md:pt-24 pb-6 md:pb-12 bg-gradient-to-b from-slate-900 to-slate-950">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-4 md:mb-8">
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-slate-50 mb-2 md:mb-4">
              Talleres de Rehabilitación
            </h1>
            <p className="text-sm md:text-lg text-slate-400 max-w-2xl mx-auto">
              Talleres terapéuticos: arte, música, meditación, yoga y más
            </p>
          </div>
          
          {/* Search Form */}
          <form onSubmit={handleSearch} className="max-w-3xl mx-auto">
            <div className="flex gap-2 md:gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 md:left-4 top-1/2 -translate-y-1/2 w-4 md:w-5 h-4 md:h-5 text-slate-500" />
                <Input
                  value={search}
                  onChange={function(e) { setSearch(e.target.value); }}
                  placeholder="Buscar talleres..."
                  className="pl-9 md:pl-12 h-10 md:h-12 bg-slate-900 border-slate-700 text-slate-200 text-sm md:text-base"
                  data-testid="workshop-search-input"
                />
              </div>
              <Button type="submit" className="h-10 md:h-12 px-4 md:px-8 bg-emerald-500 hover:bg-emerald-600" data-testid="workshop-search-btn">
                <Search className="w-4 h-4 md:mr-2" /><span className="hidden md:inline">Buscar</span>
              </Button>
            </div>
          </form>
        </div>
      </section>
      
      {/* Main Content */}
      <section className="py-6 md:py-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Filters Sidebar */}
            <aside className="lg:w-64 flex-shrink-0">
              <div className="lg:sticky lg:top-24">
                <Collapsible open={filtersOpen} onOpenChange={setFiltersOpen} className="lg:hidden mb-4">
                  <CollapsibleTrigger asChild>
                    <Button variant="outline" className="w-full justify-between border-slate-700 text-slate-300">
                      <span className="flex items-center gap-2"><Filter className="w-4 h-4" /> Filtros</span>
                      <ChevronDown className={'w-4 h-4 transition-transform ' + (filtersOpen ? 'rotate-180' : '')} />
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-4">
                    <FiltersContent
                      region={region} setRegion={setRegion}
                      workshopType={workshopType} setWorkshopType={setWorkshopType}
                      modality={modality} setModality={setModality}
                      isFree={isFree} setIsFree={setIsFree}
                      workshopTypes={workshopTypes}
                      clearFilters={clearFilters}
                      setPage={setPage}
                    />
                  </CollapsibleContent>
                </Collapsible>
                
                <div className="hidden lg:block">
                  <h3 className="text-lg font-semibold text-slate-50 mb-4 flex items-center gap-2">
                    <Filter className="w-5 h-5" /> Filtros
                  </h3>
                  <FiltersContent
                    region={region} setRegion={setRegion}
                    workshopType={workshopType} setWorkshopType={setWorkshopType}
                    modality={modality} setModality={setModality}
                    isFree={isFree} setIsFree={setIsFree}
                    workshopTypes={workshopTypes}
                    clearFilters={clearFilters}
                    setPage={setPage}
                  />
                </div>
              </div>
            </aside>
            
            {/* Results */}
            <div className="flex-1">
              <div className="flex items-center justify-between mb-6">
                <p className="text-slate-400">
                  {loading ? 'Cargando...' : total + ' talleres encontrados'}
                </p>
              </div>
              
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
                </div>
              ) : workshops.length === 0 ? (
                <div className="text-center py-20">
                  <Palette className="w-16 h-16 text-slate-700 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-400 mb-2">No se encontraron talleres</h3>
                  <p className="text-slate-500 mb-4">Intenta ajustar los filtros de búsqueda</p>
                  <Button onClick={clearFilters} variant="outline" className="border-slate-700 text-slate-300">
                    Limpiar filtros
                  </Button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {workshops.map(function(workshop) {
                      return <WorkshopCard key={workshop.workshop_id} workshop={workshop} />;
                    })}
                  </div>
                  
                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex justify-center gap-2 mt-10">
                      <Button
                        onClick={function() { setPage(Math.max(1, page - 1)); }}
                        disabled={page === 1}
                        variant="outline"
                        className="border-slate-700 text-slate-300 disabled:opacity-50"
                      >
                        Anterior
                      </Button>
                      <span className="flex items-center px-4 text-slate-400">
                        Página {page} de {totalPages}
                      </span>
                      <Button
                        onClick={function() { setPage(Math.min(totalPages, page + 1)); }}
                        disabled={page === totalPages}
                        variant="outline"
                        className="border-slate-700 text-slate-300 disabled:opacity-50"
                      >
                        Siguiente
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section - Visible for users and SEO */}
      <section className="container mx-auto px-4 pb-12 max-w-6xl">
        <h2 className="text-2xl md:text-3xl font-bold mb-6 text-center">Preguntas Frecuentes sobre Talleres Terapéuticos</h2>
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4 md:p-6">
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="ws-1" className="border-slate-700">
                <AccordionTrigger className="text-left hover:no-underline text-slate-200 hover:text-emerald-400 text-sm md:text-base">
                  ¿Qué son los talleres terapéuticos en rehabilitación de adicciones?
                </AccordionTrigger>
                <AccordionContent className="text-slate-400 text-sm">
                  Los talleres terapéuticos son actividades complementarias al tratamiento de adicciones que utilizan el arte, la música, el movimiento corporal, la meditación y otras disciplinas para promover la recuperación emocional y el bienestar. Ayudan a desarrollar nuevas habilidades, manejar el estrés y encontrar formas saludables de expresión.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="ws-2" className="border-slate-700">
                <AccordionTrigger className="text-left hover:no-underline text-slate-200 hover:text-emerald-400 text-sm md:text-base">
                  ¿Qué tipos de talleres existen para personas en recuperación?
                </AccordionTrigger>
                <AccordionContent className="text-slate-400 text-sm">
                  Existen diversos tipos de talleres: arteterapia (pintura, escultura), musicoterapia, teatro y expresión corporal, yoga y meditación, mindfulness, escritura terapéutica, deportes y actividades al aire libre, y terapia grupal. Cada uno ofrece beneficios diferentes según las necesidades de la persona.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="ws-3" className="border-slate-700">
                <AccordionTrigger className="text-left hover:no-underline text-slate-200 hover:text-emerald-400 text-sm md:text-base">
                  ¿Son efectivos los talleres como complemento al tratamiento de adicciones?
                </AccordionTrigger>
                <AccordionContent className="text-slate-400 text-sm">
                  Sí, numerosos estudios demuestran que los talleres terapéuticos mejoran significativamente los resultados del tratamiento. Ayudan a reducir la ansiedad y depresión, desarrollar habilidades sociales, encontrar nuevos intereses y pasatiempos saludables, y construir una red de apoyo con otras personas en recuperación.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="ws-4" className="border-slate-700">
                <AccordionTrigger className="text-left hover:no-underline text-slate-200 hover:text-emerald-400 text-sm md:text-base">
                  ¿Cuánto cuestan los talleres de rehabilitación en Chile?
                </AccordionTrigger>
                <AccordionContent className="text-slate-400 text-sm">
                  Los precios varían según el tipo de taller y modalidad. Existen talleres gratuitos ofrecidos por organizaciones sin fines de lucro y municipalidades. Los talleres privados cuestan entre $20.000 y $50.000 por sesión. Muchos centros de rehabilitación incluyen talleres en sus programas de tratamiento.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="ws-5" className="border-slate-700">
                <AccordionTrigger className="text-left hover:no-underline text-slate-200 hover:text-emerald-400 text-sm md:text-base">
                  ¿Puedo participar en talleres online desde cualquier lugar de Chile?
                </AccordionTrigger>
                <AccordionContent className="text-slate-400 text-sm">
                  Sí, muchos talleres ofrecen modalidad online o híbrida, permitiendo participar desde cualquier ciudad de Chile. Los talleres de meditación, mindfulness, escritura terapéutica y algunos de arte se adaptan muy bien al formato virtual. Filtra por 'Online' en nuestro directorio para encontrarlos.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardContent>
        </Card>
      </section>
      
      <Footer />
    </div>
  );
}

// Filters Component
function FiltersContent({ region, setRegion, workshopType, setWorkshopType, modality, setModality, isFree, setIsFree, workshopTypes, clearFilters, setPage }) {
  
  function handleRegionChange(value) {
    setRegion(value);
    if (setPage) setPage(1);
  }
  
  function handleTypeChange(value) {
    setWorkshopType(value);
    if (setPage) setPage(1);
  }
  
  function handleModalityChange(value) {
    setModality(value);
    if (setPage) setPage(1);
  }
  
  function handleFreeChange(value) {
    setIsFree(value);
    if (setPage) setPage(1);
  }
  
  return (
    <div className="space-y-6">
      {/* Region */}
      <div>
        <Label className="text-slate-300 mb-2 block">Región</Label>
        <Select value={region || 'all'} onValueChange={handleRegionChange}>
          <SelectTrigger className="bg-slate-900 border-slate-700 text-slate-200" data-testid="filter-region">
            <SelectValue placeholder="Todas las regiones" />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700">
            <SelectItem value="all" className="text-slate-200">Todas las regiones</SelectItem>
            {CHILEAN_REGIONS.map(function(r) {
              return <SelectItem key={r.value} value={r.value} className="text-slate-200">{r.label}</SelectItem>;
            })}
          </SelectContent>
        </Select>
      </div>
      
      {/* Workshop Type */}
      <div>
        <Label className="text-slate-300 mb-2 block">Tipo de taller</Label>
        <Select value={workshopType || 'all'} onValueChange={handleTypeChange}>
          <SelectTrigger className="bg-slate-900 border-slate-700 text-slate-200" data-testid="filter-type">
            <SelectValue placeholder="Todos los tipos" />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700">
            <SelectItem value="all" className="text-slate-200">Todos los tipos</SelectItem>
            {workshopTypes.map(function(t) {
              var info = workshopTypeLabels[t] || { label: t };
              return <SelectItem key={t} value={t} className="text-slate-200">{info.label}</SelectItem>;
            })}
          </SelectContent>
        </Select>
      </div>
      
      {/* Modality */}
      <div>
        <Label className="text-slate-300 mb-2 block">Modalidad</Label>
        <Select value={modality || 'all'} onValueChange={handleModalityChange}>
          <SelectTrigger className="bg-slate-900 border-slate-700 text-slate-200" data-testid="filter-modality">
            <SelectValue placeholder="Todas las modalidades" />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700">
            <SelectItem value="all" className="text-slate-200">Todas las modalidades</SelectItem>
            <SelectItem value="presencial" className="text-slate-200">Presencial</SelectItem>
            <SelectItem value="online" className="text-slate-200">Online</SelectItem>
            <SelectItem value="hibrido" className="text-slate-200">Híbrido</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      {/* Free Filter */}
      <div>
        <Label className="text-slate-300 mb-2 block">Precio</Label>
        <div className="flex gap-2">
          <Button
            type="button"
            variant={isFree === null ? 'default' : 'outline'}
            size="sm"
            onClick={function() { handleFreeChange(null); }}
            className={isFree === null ? 'bg-emerald-500' : 'border-slate-700 text-slate-300'}
          >
            Todos
          </Button>
          <Button
            type="button"
            variant={isFree === true ? 'default' : 'outline'}
            size="sm"
            onClick={function() { handleFreeChange(true); }}
            className={isFree === true ? 'bg-emerald-500' : 'border-slate-700 text-slate-300'}
          >
            Gratuitos
          </Button>
          <Button
            type="button"
            variant={isFree === false ? 'default' : 'outline'}
            size="sm"
            onClick={function() { handleFreeChange(false); }}
            className={isFree === false ? 'bg-emerald-500' : 'border-slate-700 text-slate-300'}
          >
            De pago
          </Button>
        </div>
      </div>
      
      {/* Clear Filters */}
      <Button onClick={clearFilters} variant="outline" className="w-full border-slate-700 text-slate-300 hover:bg-slate-800">
        Limpiar filtros
      </Button>
    </div>
  );
}

// Workshop Detail Component
export function WorkshopDetail() {
  var { workshopId: paramId } = useParams();
  var [workshop, setWorkshop] = useState(null);
  var [loading, setLoading] = useState(true);
  var [reviews, setReviews] = useState([]);
  var [showAllReviews, setShowAllReviews] = useState(false);
  var REVIEWS_VISIBLE_COUNT = 5;
  var [showReviewForm, setShowReviewForm] = useState(false);
  var [showContactForm, setShowContactForm] = useState(false);
  var [reviewForm, setReviewForm] = useState({ author_name: '', rating: 5, comment: '' });
  var [contactForm, setContactForm] = useState({ name: '', email: '', phone: '', message: '' });
  var [sending, setSending] = useState(false);
  var [showPhone, setShowPhone] = useState(false);
  var [workshopId, setWorkshopId] = useState(null);

  function handleRevealPhone() {
    setShowPhone(true);
    if (workshopId) trackPhoneReveal('workshop', workshopId);
  }

  useEffect(function() {
    loadWorkshop();
  }, [paramId]);

  useEffect(function() {
    if (workshopId) loadReviews();
  }, [workshopId]);

  async function loadWorkshop() {
    setLoading(true);
    try {
      var res;
      // Check if paramId is a slug (no underscore) or workshop_id (has underscore)
      if (paramId.startsWith('workshop_')) {
        res = await axios.get(API + '/workshops/public/' + paramId);
      } else {
        res = await axios.get(API + '/workshops/by-slug/' + paramId);
      }
      setWorkshop(res.data);
      setWorkshopId(res.data.workshop_id);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  async function loadReviews() {
    if (!workshopId) return;
    try {
      var res = await axios.get(API + '/workshops/' + workshopId + '/reviews');
      setReviews(res.data.reviews || []);
    } catch (e) {
      console.error(e);
    }
  }

  async function handleReviewSubmit(e) {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(API + '/workshops/' + workshopId + '/reviews', reviewForm);
      setShowReviewForm(false);
      setReviewForm({ author_name: '', rating: 5, comment: '' });
      loadReviews();
      loadWorkshop();
    } catch (e) {
      console.error(e);
    } finally {
      setSending(false);
    }
  }

  async function handleContactSubmit(e) {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(API + '/workshops/' + workshopId + '/contact', contactForm);
      setShowContactForm(false);
      setContactForm({ name: '', email: '', phone: '', message: '' });
      alert('Mensaje enviado correctamente');
    } catch (e) {
      console.error(e);
    } finally {
      setSending(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (!workshop) {
    return (
      <div className="min-h-screen bg-slate-950">
        <title>Taller no encontrado (404) | SinAdicciones.org</title>
        <meta name="robots" content="noindex, follow" />
        <meta name="description" content="El taller que buscas no existe o ha sido removido del directorio." />
        <Header />
        <div className="pt-24 pb-12 text-center">
          <h1 className="text-2xl font-bold text-slate-50 mb-4">Taller no encontrado</h1>
          <Link to="/talleres">
            <Button variant="outline" className="border-slate-700 text-slate-300">
              <ArrowLeft className="w-4 h-4 mr-2" /> Volver al directorio
            </Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  var typeInfo = workshopTypeLabels[workshop.workshop_type] || workshopTypeLabels.otro;
  var TypeIcon = typeInfo.icon;

  return (
    <div className="min-h-screen bg-slate-950" data-testid="workshop-detail">
      
        <title>{workshop.name + ' | Talleres | Sin Adicciones'}</title>
        <meta name="description" content={workshop.description || 'Taller de rehabilitación en Chile'} />
        <link rel="canonical" href={'https://sinadicciones.org/talleres/' + (workshop.slug || workshop.workshop_id)} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={workshop.name + ' | Talleres terapéuticos'} />
        <meta property="og:description" content={workshop.description || 'Taller de rehabilitación en Chile'} />
        <meta property="og:url" content={'https://sinadicciones.org/talleres/' + (workshop.slug || workshop.workshop_id)} />
        <meta property="og:image" content={workshop.logo ? getImageUrl(workshop.logo) : 'https://sinadicciones.org/og-image.png'} />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={workshop.name} />
        <meta name="twitter:description" content={workshop.description || 'Taller terapéutico en Chile'} />
        <meta name="twitter:image" content={workshop.logo ? getImageUrl(workshop.logo) : 'https://sinadicciones.org/og-image.png'} />
      
      
      <Header />
      
      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          {/* Back Button */}
          <Link to="/talleres" className="inline-flex items-center gap-2 text-slate-400 hover:text-slate-200 mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Volver al directorio
          </Link>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Header Card */}
              <Card className="bg-slate-900/50 border-slate-800 overflow-hidden">
                {/* Image/Logo */}
                {(workshop.logo || workshop.gallery?.length > 0) && (
                  <div className="aspect-video bg-slate-800 relative">
                    <img 
                      src={getImageUrl(workshop.logo || (workshop.gallery && workshop.gallery[0]))} 
                      alt={workshop.name} 
                      className="w-full h-full object-cover" 
                    />
                    {/* Badges */}
                    <div className="absolute top-4 left-4 flex gap-2">
                      {workshop.is_premium && (
                        <span className="px-3 py-1.5 bg-amber-500/90 text-amber-950 text-sm font-bold rounded-lg flex items-center gap-1">
                          <Crown className="w-4 h-4" /> Premium
                        </span>
                      )}
                      {workshop.is_verified && (
                        <span className="px-3 py-1.5 bg-emerald-500/90 text-emerald-950 text-sm font-bold rounded-lg flex items-center gap-1">
                          <BadgeCheck className="w-4 h-4" /> Verificado
                        </span>
                      )}
                    </div>
                  </div>
                )}
                
                <CardContent className="p-6 md:p-8">
                  {/* Type and Modality */}
                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <span className="px-3 py-1.5 bg-purple-500/20 text-purple-400 rounded-lg flex items-center gap-2">
                      <TypeIcon className="w-4 h-4" />
                      {typeInfo.label}
                    </span>
                    <span className="px-3 py-1.5 bg-slate-800 text-slate-300 rounded-lg flex items-center gap-2">
                      {workshop.modality === 'online' ? <Video className="w-4 h-4" /> : <MapPin className="w-4 h-4" />}
                      {modalityLabels[workshop.modality] || 'Presencial'}
                    </span>
                    {workshop.is_free && (
                      <span className="px-3 py-1.5 bg-green-500/20 text-green-400 rounded-lg">
                        Gratuito
                      </span>
                    )}
                  </div>
                  
                  {/* Name */}
                  <h1 className="text-2xl md:text-3xl font-bold text-slate-50 mb-4">{workshop.name}</h1>
                  
                  {/* Rating */}
                  {workshop.average_rating > 0 && (
                    <div className="flex items-center gap-3 mb-4">
                      <StarRating rating={workshop.average_rating} />
                      <span className="text-slate-400">
                        {workshop.average_rating.toFixed(1)} ({workshop.total_reviews} reseñas)
                      </span>
                    </div>
                  )}
                  
                  {/* Description */}
                  {workshop.description && (
                    <p className="text-slate-300 leading-relaxed">{workshop.description}</p>
                  )}
                </CardContent>
              </Card>
              
              {/* Details */}
              <Card className="bg-slate-900/50 border-slate-800">
                <CardContent className="p-6 md:p-8">
                  <h2 className="text-xl font-semibold text-slate-50 mb-6">Detalles del Taller</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Schedule */}
                    {workshop.schedule && (
                      <div className="flex items-start gap-3">
                        <Calendar className="w-5 h-5 text-emerald-400 mt-0.5" />
                        <div>
                          <p className="text-slate-400 text-sm">Horario</p>
                          <p className="text-slate-200">{workshop.schedule}</p>
                        </div>
                      </div>
                    )}
                    
                    {/* Duration */}
                    {workshop.duration && (
                      <div className="flex items-start gap-3">
                        <Clock className="w-5 h-5 text-emerald-400 mt-0.5" />
                        <div>
                          <p className="text-slate-400 text-sm">Duración</p>
                          <p className="text-slate-200">{workshop.duration}</p>
                        </div>
                      </div>
                    )}
                    
                    {/* Price */}
                    <div className="flex items-start gap-3">
                      <DollarSign className="w-5 h-5 text-emerald-400 mt-0.5" />
                      <div>
                        <p className="text-slate-400 text-sm">Precio</p>
                        {workshop.is_free ? (
                          <p className="text-green-400 font-medium">Gratuito</p>
                        ) : workshop.price ? (
                          <p className="text-slate-200">
                            ${workshop.price.toLocaleString('es-CL')}
                            {workshop.price_description && <span className="text-slate-500"> /{workshop.price_description}</span>}
                          </p>
                        ) : (
                          <p className="text-slate-400">Consultar</p>
                        )}
                      </div>
                    </div>
                    
                    {/* Location */}
                    {(workshop.address || workshop.city) && (
                      <div className="flex items-start gap-3">
                        <MapPin className="w-5 h-5 text-emerald-400 mt-0.5" />
                        <div>
                          <p className="text-slate-400 text-sm">Ubicación</p>
                          <p className="text-slate-200">
                            {workshop.address && <span>{workshop.address}, </span>}
                            {workshop.city}
                            {workshop.region && <span>, {workshop.region}</span>}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Additional Services */}
                  {workshop.additional_services?.length > 0 && (
                    <div className="mt-8 pt-6 border-t border-slate-800">
                      <h3 className="text-lg font-semibold text-slate-50 mb-4">Servicios Adicionales</h3>
                      <div className="flex flex-wrap gap-2">
                        {workshop.additional_services.map(function(service, i) {
                          return (
                            <span key={i} className="px-3 py-1.5 bg-slate-800 text-slate-300 rounded-lg text-sm flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-emerald-400" />
                              {service}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Video */}
              {workshop.youtube_video && (
                <Card className="bg-slate-900/50 border-slate-800">
                  <CardContent className="p-6 md:p-8">
                    <h2 className="text-xl font-semibold text-slate-50 mb-6">Video</h2>
                    <YouTubeEmbed url={workshop.youtube_video} />
                  </CardContent>
                </Card>
              )}
              
              {/* Gallery */}
              {workshop.gallery?.length > 0 && (
                <Card className="bg-slate-900/50 border-slate-800">
                  <CardContent className="p-6 md:p-8">
                    <h2 className="text-xl font-semibold text-slate-50 mb-6">Galería</h2>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {workshop.gallery.map(function(img, i) {
                        return (
                          <div key={i} className="aspect-video rounded-lg overflow-hidden bg-slate-800">
                            <img src={img} alt={'Imagen ' + (i + 1)} className="w-full h-full object-cover" />
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Reviews */}
              <Card className="bg-slate-900/50 border-slate-800">
                <CardContent className="p-6 md:p-8">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-slate-50">Reseñas</h2>
                    <Button onClick={function() { setShowReviewForm(!showReviewForm); }} variant="outline" className="border-slate-700 text-slate-300">
                      Escribir reseña
                    </Button>
                  </div>
                  
                  {/* Review Form */}
                  {showReviewForm && (
                    <form onSubmit={handleReviewSubmit} className="mb-8 p-4 bg-slate-800 rounded-xl space-y-4">
                      <div>
                        <Label className="text-slate-300">Tu nombre</Label>
                        <Input
                          value={reviewForm.author_name}
                          onChange={function(e) { setReviewForm(Object.assign({}, reviewForm, { author_name: e.target.value })); }}
                          required
                          className="bg-slate-900 border-slate-700 text-slate-200"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">Calificación</Label>
                        <div className="mt-2">
                          <StarRating 
                            rating={reviewForm.rating} 
                            onRate={function(r) { setReviewForm(Object.assign({}, reviewForm, { rating: r })); }} 
                            interactive 
                            size="lg" 
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-slate-300">Comentario</Label>
                        <Textarea
                          value={reviewForm.comment}
                          onChange={function(e) { setReviewForm(Object.assign({}, reviewForm, { comment: e.target.value })); }}
                          required
                          rows={4}
                          className="bg-slate-900 border-slate-700 text-slate-200"
                        />
                      </div>
                      <Button type="submit" disabled={sending} className="bg-emerald-500 hover:bg-emerald-600">
                        {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        Enviar reseña
                      </Button>
                    </form>
                  )}
                  
                  {/* Reviews List */}
                  {reviews.length === 0 ? (
                    <p className="text-slate-500 text-center py-8">Aún no hay reseñas. ¡Sé el primero en opinar!</p>
                  ) : (
                    <div className="space-y-4">
                      {(showAllReviews ? reviews : reviews.slice(0, REVIEWS_VISIBLE_COUNT)).map(function(review, i) {
                        return (
                          <div key={i} className="p-4 bg-slate-800/50 rounded-xl">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                {review.reviewer_photo && (
                                  <img src={review.reviewer_photo} alt="" className="w-8 h-8 rounded-full object-cover" />
                                )}
                                <span className="font-medium text-slate-200">{review.author_name || review.reviewer_name}</span>
                                {(review.source === 'google_places' || review.source === 'google') && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs font-medium rounded-full">
                                    <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                                    </svg>
                                    Google
                                  </span>
                                )}
                              </div>
                              <StarRating rating={review.rating} size="sm" />
                            </div>
                            {review.relative_time && <p className="text-slate-500 text-xs mb-2">{review.relative_time}</p>}
                            <p className="text-slate-400">{review.comment}</p>
                          </div>
                        );
                      })}
                      {reviews.length > REVIEWS_VISIBLE_COUNT && !showAllReviews && (
                        <button 
                          onClick={function() { setShowAllReviews(true); }}
                          className="w-full py-3 text-center text-emerald-400 hover:text-emerald-300 font-medium border border-slate-700 rounded-xl hover:bg-slate-800/50 transition-colors"
                          data-testid="reviews-show-more"
                        >
                          Ver más reseñas ({reviews.length - REVIEWS_VISIBLE_COUNT} más)
                        </button>
                      )}
                      {showAllReviews && reviews.length > REVIEWS_VISIBLE_COUNT && (
                        <button 
                          onClick={function() { setShowAllReviews(false); }}
                          className="w-full py-3 text-center text-slate-400 hover:text-slate-300 font-medium border border-slate-700 rounded-xl hover:bg-slate-800/50 transition-colors"
                          data-testid="reviews-show-less"
                        >
                          Ver menos
                        </button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
            
            {/* Sidebar */}
            <div className="space-y-6">
              {/* Contact Card */}
              <Card className="bg-slate-900/50 border-slate-800 sticky top-24">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-slate-50 mb-4">Contacto</h3>
                  
                  <div className="space-y-3">
                    {/* WhatsApp */}
                    {workshop.whatsapp && (
                      <WhatsAppButton number={workshop.whatsapp} name={workshop.name} />
                    )}
                    
                    {/* Phone - Hidden until clicked */}
                    {workshop.phone && (
                      showPhone ? (
                        <a href={'tel:' + workshop.phone} onClick={function() { trackCallClick('workshop', workshopId); }} className="flex items-center gap-3 p-4 bg-slate-800 rounded-xl hover:bg-slate-700 transition-colors duration-200 text-slate-300">
                          <Phone className="w-5 h-5 text-emerald-400" /><span>{workshop.phone}</span>
                        </a>
                      ) : (
                        <button onClick={handleRevealPhone} className="flex items-center gap-3 p-4 bg-emerald-600 hover:bg-emerald-700 rounded-xl transition-colors duration-200 text-white w-full">
                          <Eye className="w-5 h-5" /><span>Ver Número de Teléfono</span>
                        </button>
                      )
                    )}
                    
                    {/* Website */}
                    {workshop.website && (
                      <a href={workshop.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-4 bg-slate-800 rounded-xl hover:bg-slate-700 transition-colors duration-200 text-slate-300">
                        <Globe className="w-5 h-5 text-emerald-400" /><span>Sitio web</span><ExternalLink className="w-4 h-4 ml-auto" />
                      </a>
                    )}
                    
                    {/* Google Maps */}
                    <GoogleMapsLink address={workshop.address} city={workshop.city} />
                  </div>
                  
                  {/* Contact Form Button */}
                  <Button 
                    onClick={function() { setShowContactForm(!showContactForm); }} 
                    className="w-full mt-4 bg-emerald-500 hover:bg-emerald-600"
                    data-testid="workshop-contact-btn"
                  >
                    <Send className="w-4 h-4 mr-2" /> Enviar mensaje
                  </Button>
                  
                  {/* Contact Form */}
                  {showContactForm && (
                    <form onSubmit={handleContactSubmit} className="mt-4 space-y-3">
                      <Input
                        placeholder="Tu nombre"
                        value={contactForm.name}
                        onChange={function(e) { setContactForm(Object.assign({}, contactForm, { name: e.target.value })); }}
                        required
                        className="bg-slate-800 border-slate-700 text-slate-200"
                      />
                      <Input
                        type="email"
                        placeholder="Tu email"
                        value={contactForm.email}
                        onChange={function(e) { setContactForm(Object.assign({}, contactForm, { email: e.target.value })); }}
                        required
                        className="bg-slate-800 border-slate-700 text-slate-200"
                      />
                      <Input
                        placeholder="Tu teléfono"
                        value={contactForm.phone}
                        onChange={function(e) { setContactForm(Object.assign({}, contactForm, { phone: e.target.value })); }}
                        className="bg-slate-800 border-slate-700 text-slate-200"
                      />
                      <Textarea
                        placeholder="Tu mensaje"
                        value={contactForm.message}
                        onChange={function(e) { setContactForm(Object.assign({}, contactForm, { message: e.target.value })); }}
                        required
                        rows={3}
                        className="bg-slate-800 border-slate-700 text-slate-200"
                      />
                      <Button type="submit" disabled={sending} className="w-full bg-emerald-500 hover:bg-emerald-600">
                        {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        Enviar
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}

export default WorkshopsDirectory;
